import { useState, useEffect, useRef } from "react";
import { 
  subscribeToVendors, 
  subscribeToVendorCategories, 
  subscribeToProductTags,
  subscribeToBrandTags,
  seedDefaultVendorsIfEmpty,
  addVendor,
  updateVendor,
  deleteVendor
} from "../services/vendorService";

export function useVendors() {
  const [vendors, setVendors] = useState([]);
  const [categories, setCategories] = useState([]);
  const [availableTags, setAvailableTags] = useState([]);
  const [availableBrandTags, setAvailableBrandTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  
  // To avoid seeding multiple times if component re-renders quickly
  const hasSeededRef = useRef(false);

  useEffect(() => {
    let unsubscribeVendors = () => {};
    let unsubscribeCategories = () => {};
    let unsubscribeTags = () => {};
    let unsubscribeBrandTags = () => {};

    try {
      setLoading(true);

      // Subscribe to all collections
      unsubscribeVendors = subscribeToVendors(async (data, err) => {
        if (err) {
          setError("Failed to load vendors.");
          setLoading(false);
          return;
        }

        // If list is empty, seed defaults
        if (data.length === 0 && !hasSeededRef.current) {
          hasSeededRef.current = true;
          await seedDefaultVendorsIfEmpty(data);
          // the seeding will trigger another snapshot, which will update state
        } else {
          setVendors(data);
          setLoading(false);
        }
      });

      unsubscribeCategories = subscribeToVendorCategories((data, err) => {
        if (!err) setCategories(data);
      });

      unsubscribeTags = subscribeToProductTags((data, err) => {
        if (!err) setAvailableTags(data);
      });

      unsubscribeBrandTags = subscribeToBrandTags((data, err) => {
        if (!err) setAvailableBrandTags(data);
      });

    } catch (err) {
      console.error(err);
      setError("Failed to initialize vendor subscriptions.");
      setLoading(false);
    }

    return () => {
      unsubscribeVendors();
      unsubscribeCategories();
      unsubscribeTags();
      unsubscribeBrandTags();
    };
  }, []);

  const handleAddVendor = async (vendorData) => {
    try {
      setError("");
      await addVendor(vendorData);
    } catch (err) {
      console.error("Error adding vendor:", err);
      setError(err.message || "Failed to add vendor.");
      throw err;
    }
  };

  const handleUpdateVendor = async (id, vendorData) => {
    try {
      setError("");
      await updateVendor(id, vendorData);
    } catch (err) {
      console.error("Error updating vendor:", err);
      setError(err.message || "Failed to update vendor.");
      throw err;
    }
  };

  const handleDeleteVendor = async (id) => {
    try {
      setError("");
      await deleteVendor(id);
    } catch (err) {
      console.error("Error deleting vendor:", err);
      setError(err.message || "Failed to delete vendor.");
      throw err;
    }
  };

  return {
    vendors,
    categories,
    availableTags,
    brandTags: availableBrandTags,
    availableBrandTags,
    loading,
    error,
    setError,
    addVendor: handleAddVendor,
    updateVendor: handleUpdateVendor,
    deleteVendor: handleDeleteVendor
  };
}
